









import { useEffect, useState } from "react";
import { getEmployeesApi, deleteUserApi } from "../../app/api";
import EditUser from "../employees/EditUser";

import { useNavigate } from "react-router-dom";
import { ArrowDown, ArrowUp, ArrowUpDown, Search, User } from "lucide-react";
import { createPortal } from "react-dom";

import ChatDrawer from "../chat/ChatDrawer";

const IMAGE_BASE_URL = "https://uptask.visiontrek.io";

const extractEmployeeList = (response) => {
  const queue = [response];
  const seen = new Set();
  const arrays = [];

  while (queue.length) {
    const candidate = queue.shift();
    if (!candidate || typeof candidate !== "object" || seen.has(candidate)) continue;

    seen.add(candidate);

    if (Array.isArray(candidate)) {
      arrays.push(candidate);
      queue.push(...candidate);
    } else {
      queue.push(...Object.values(candidate));
    }
  }

  return (
    arrays.find((items) =>
      items.some((item) =>
        item &&
        typeof item === "object" &&
        (item.first_name || item.last_name || item.name || item.email || item.emp_id || item.user_id)
      )
    ) || []
  );
};
	
export default function Employees() {

  const [openChat, setOpenChat] = useState(false);


  const [editUser, setEditUser] = useState(null);
  const navigate = useNavigate();

  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "name",
    direction: "asc",
  });

  const [deleteUser, setDeleteUser] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchEmployees = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getEmployeesApi({
        page: 1,
        limit: 1000,
      });
      if (res?.status === 401 || res?.forbidden) {
        setEmployees([]);
        setError("Login again on localhost to load employees.");
        return;
      }

      setEmployees(extractEmployeeList(res));
    } catch {
      setEmployees([]);
      setError("Unable to load employees.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    const keyword = search.toLowerCase().trim();
    if (!keyword) {
      setFilteredEmployees(employees);
      return;
    }

    const filtered = employees.filter((emp) => {
      const searchableText = [
        getEmployeeName(emp),
        getRollNumber(emp),
        emp.email,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return (
        searchableText.includes(keyword)
      );
    });

    setFilteredEmployees(filtered);
  }, [search, employees]);

  const confirmDelete = async () => {
    if (!deleteUser) return;
    setDeleting(true);
    try {
      const res = await deleteUserApi(deleteUser.id);
      if (res.success) {
        setDeleteUser(null);
        fetchEmployees();
      }
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN") : "—");

  const getEmployeeName = (emp) =>
    emp.name ||
    emp.employee_name ||
    `${emp.first_name || ""} ${emp.last_name || ""}`.trim() ||
    "—";

  const getRollNumber = (emp) =>
    emp.roll_number ||
    emp.roll_no ||
    emp.employee_code ||
    emp.employee_id ||
    emp.emp_code ||
    emp.id ||
    "";

  const getSortValue = (emp, key) => {
    const sortMap = {
      roll: getRollNumber(emp),
      name: getEmployeeName(emp),
      email: emp.email,
      role: emp.role_name,
      job: emp.job_title,
      joined: emp.created_at,
    };

    const value = sortMap[key] ?? "";
    if (key === "joined") {
      return new Date(value).getTime() || 0;
    }

    return String(value).toLowerCase();
  };

  const handleSort = (key) => {
    setSortConfig((current) => ({
      key,
      direction:
        current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
  };

    const sortedEmployees = [...filteredEmployees].sort((a, b) => {
      const valueA = getSortValue(a, sortConfig.key);
      const valueB = getSortValue(b, sortConfig.key);

      if (typeof valueA === "number" && typeof valueB === "number") {
        return sortConfig.direction === "asc"
          ? valueA - valueB
          : valueB - valueA;
      }

      return sortConfig.direction === "asc"
        ? valueA.localeCompare(valueB, undefined, { numeric: true })
        : valueB.localeCompare(valueA, undefined, { numeric: true });
    });
    

  return (
    <>
      <div className="
        rounded-2xl p-5 md:mt-20
        bg-[#12082F]
        text-white border border-white/10
        shadow-2xl
      ">
        {/* Header */}
        <div className="flex flex-col  justify-between items-start mb-4 gap-2">
          {/* <h3 className="font-bold text-2xl">Employees</h3> */}

          <div className="flex justify-between items-center w-full">

<h3 className="font-bold text-2xl">Employees</h3>

<button
  onClick={() => setOpenChat(true)}
  className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-sm"
>
  Chat
</button>

</div>
          <div className="relative w-full max-w-sm">
            <Search
              size={16}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-white/45"
            />
            <input
              placeholder="Search by name, roll no or email"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-xl border border-white/10 bg-white/10 px-10 py-2.5 text-sm text-white shadow-lg outline-none transition placeholder:text-white/45 hover:border-white/20 focus:border-cyan-300/50 focus:ring-2 focus:ring-cyan-300/15"
            />
          </div>
        </div>

        {/* Table */}
       <div className="overflow-x-auto rounded-md">
          <table className="min-w-[820px] w-full border-collapse rounded-md text-sm shadow-lg">
            <thead className=" text-white border-b border-white/10">
              <tr>
                <SortableHeader
                  label="Roll No"
                  sortKey="roll"
                  className="w-[100px]"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
                <SortableHeader
                  label="Name"
                  sortKey="name"
                  className="w-[240px]"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
                <SortableHeader
                  label="Email"
                  sortKey="email"
                  className="w-[260px]"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
                <SortableHeader
                  label="Role"
                  sortKey="role"
                  className="w-[110px]"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
                <SortableHeader
                  label="Job"
                  sortKey="job"
                  className="w-[180px]"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
                <SortableHeader
                  label="Joined"
                  sortKey="joined"
                  className="w-[100px]"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
                <th className="text-left w-[80px] ">Edit</th>
                <th className="text-left w-[90px] ">Delete</th>
              </tr>
            </thead>

            <tbody>
              {loading && (
                <tr>
                  <td colSpan="8" className="py-6 text-center opacity-60">
                    Loading...
                  </td>
                </tr>
              )}

	              {!loading &&
	                // filteredEmployees.map((emp) => (
	                  sortedEmployees.map((emp) => (

                    <tr
                    key={emp.id || emp.user_id || emp.employee_id || emp.emp_id || `${getEmployeeName(emp)}-${getRollNumber(emp)}`}
                    className="border-b py-5 border-white/5 hover:bg-white/5"
                  >
                    <td className="w-[100px] py-2 text-xs font-semibold text-white/70">
                      {getRollNumber(emp)}
                    </td>

                    {/* Name */}
                    <td className=" py-2 w-[240px]">
                      <div
                        className="flex items-center gap-3 cursor-pointer"
	                        onClick={() => navigate(`/users/${emp.id || emp.user_id || emp.employee_id || emp.emp_id}`, { state: { user: emp } })}
                      >
                        <div className="w-8 h-8 rounded-full bg-white/20 overflow-hidden">
                          {emp.profile_pic ? (
  <img
    src={`${IMAGE_BASE_URL}${emp.profile_pic}`}
    className="w-full h-full object-cover"
    alt={getEmployeeName(emp)}
  />
) : (
  <div className="w-full h-full flex items-center justify-center bg-white/10">
    <User size={18} className="text-white/60" />
  </div>
)}

                        </div>

                        <span className="font-medium truncate">
                          {getEmployeeName(emp)}
                        </span>
                      </div>
                    </td>

                    {/* Email */}
                    <td className="w-[260px] truncate opacity-80">
                      {emp.email}
                    </td>

                    {/* Role */}
                    <td className="w-[110px]">
                      <span className="
                        px-2 py-1 rounded-full
                        bg-blue-500/20 text-blue-300
                        text-xs
                      ">
                        {emp.role_name}
                      </span>
                    </td>

                    {/* Job */}
                    <td className="w-[180px] truncate">
                      {emp.job_title || "—"}
                    </td>

                    {/* Joined */}
                    <td className="w-[100px] text-xs opacity-70">
                      {formatDate(emp.created_at)}
                    </td>

                    {/* Edit */}
                    <td className="w-[80px] text-center">
                      <button
                        // onClick={() => navigate(`/users/${emp.id}/edit`)}
                        onClick={() => setEditUser(emp)}
                        className="px-3 py-1 rounded bg-white/10 hover:bg-white/20 text-xs"
                      >
                        Edit
                      </button>
                    </td>

                    {/* Delete */}
                    <td className="w-[90px] text-center">
                      <button
                        onClick={() => setDeleteUser(emp)}
                        className="px-3 py-1 rounded bg-red-500/20 text-red-300 hover:bg-red-500 hover:text-white text-xs"
                      >
                        Delete
                      </button>
                    </td>
	                  </tr>
	                ))}
              {!loading && (error || sortedEmployees.length === 0) && (
                <tr>
                  <td colSpan="8" className="py-8 text-center text-white/55">
                    {error || "No employees found"}
                  </td>
                </tr>
              )}
	            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Modal */}
    
{deleteUser &&
  createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-md">
      
      <div
        className="
          relative
          w-full max-w-md
          p-8
          rounded-2xl
          bg-white/10
          backdrop-blur-xl
          border border-white/20
          shadow-[0_0_40px_rgba(0,0,0,0.6)]
          text-white
        "
      >
        {/* Soft glow border effect */}
        <div className="absolute inset-0 rounded-2xl border border-white/10 pointer-events-none"></div>

        <h3 className="text-xl font-semibold mb-3 tracking-wide">
          Delete Employee
        </h3>

        <p className="text-sm text-white/70 mb-8">
          Are you sure you want to delete{" "}
          <span className="font-medium text-white">
            {deleteUser.first_name} {deleteUser.last_name}
          </span>
          ?
        </p>

        <div className="flex justify-end gap-4">
          <button
            onClick={() => setDeleteUser(null)}
            className="
              px-6 py-2 rounded-lg
              bg-white/15
              hover:bg-white/25
              transition-all duration-200
            "
          >
            Cancel
          </button>

          <button
            onClick={confirmDelete}
            disabled={deleting}
            className="
              px-6 py-2 rounded-lg
              bg-red-500/80
              hover:bg-red-600
              transition-all duration-200
              disabled:opacity-50
            "
          >
            {deleting ? "Deleting..." : "Delete"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )}



{editUser && (
  <EditUser
    user={editUser}
    onClose={() => setEditUser(null)}
    onUpdated={() => {
      setEditUser(null);
      fetchEmployees(); // refresh table after update
    }}
  />
)}

{openChat && (
  <ChatDrawer
    employees={employees}
    onClose={() => setOpenChat(false)}
  />
)}

    </>
  );
}

function SortableHeader({ label, sortKey, sortConfig, onSort, className = "" }) {
  const isActive = sortConfig.key === sortKey;
  const SortIcon = !isActive
    ? ArrowUpDown
    : sortConfig.direction === "asc"
    ? ArrowUp
    : ArrowDown;

  return (
    <th className={`text-left ${className}`}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-left text-xs font-bold transition ${
          isActive
            ? "bg-cyan-300/12 text-cyan-100"
            : "text-white hover:bg-white/8"
        }`}
      >
        {label}
        <SortIcon size={13} className={isActive ? "text-cyan-200" : "text-white/45"} />
      </button>
    </th>
  );
}







// import { useEffect, useState } from "react";
// import { getEmployeesApi, deleteUserApi } from "../../app/api";
// import EditUser from "../employees/EditUser";

// import { useNavigate } from "react-router-dom";
// import { User } from "lucide-react";
// import { createPortal } from "react-dom";
// const IMAGE_BASE_URL = "https://uptask.visiontrek.io";

// export default function Employees() {
//   const [editUser, setEditUser] = useState(null);
//   const navigate = useNavigate();

//   const [employees, setEmployees] = useState([]);
//   const [filteredEmployees, setFilteredEmployees] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [search, setSearch] = useState("");

//   const [deleteUser, setDeleteUser] = useState(null);
//   const [deleting, setDeleting] = useState(false);

//   const fetchEmployees = async () => {
//     setLoading(true);
//     try {
//       const res = await getEmployeesApi({
//         page: 1,
//         limit: 1000,
//         search,
//         role_id: 2,
//       });
//       setEmployees(res.success ? res.data || [] : []);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchEmployees();
//   }, [search]);

//   useEffect(() => {
//     const keyword = search.toLowerCase().trim();
//     if (!keyword) {
//       setFilteredEmployees(employees);
//       return;
//     }

//     const filtered = employees.filter((emp) => {
//       const fullName =
//         `${emp.first_name} ${emp.last_name}`.toLowerCase();
//       return (
//         fullName.includes(keyword) ||
//         emp.email?.toLowerCase().includes(keyword)
//       );
//     });

//     setFilteredEmployees(filtered);
//   }, [search, employees]);

//   const confirmDelete = async () => {
//     if (!deleteUser) return;
//     setDeleting(true);
//     try {
//       const res = await deleteUserApi(deleteUser.id);
//       if (res.success) {
//         setDeleteUser(null);
//         fetchEmployees();
//       }
//     } finally {
//       setDeleting(false);
//     }
//   };

//   const formatDate = (d) =>
//     new Date(d).toLocaleDateString("en-IN");

//     const sortedEmployees = [...filteredEmployees].sort((a, b) => {
//       const nameA = `${a.first_name || ""} ${a.last_name || ""}`.toLowerCase();
//       const nameB = `${b.first_name || ""} ${b.last_name || ""}`.toLowerCase();
//       return nameA.localeCompare(nameB);
//     });
    

//   return (
//     <>
//       <div className="
//         rounded-2xl p-5 md:mt-20
//         bg-gradient-to-br
//         from-[#2d1c6a] via-[#24155a] to-[#12082f]
//         text-white border border-white/10
//         shadow-2xl
//       ">
//         {/* Header */}
//         <div className="flex flex-col  justify-between items-start mb-4 gap-2">
//           <h3 className="font-bold text-2xl">Employees</h3>
//           <input
//             placeholder="Search by name or email"
//             value={search}
//             onChange={(e) => setSearch(e.target.value)}
//             className="px-10 py-1.5 rounded-md bg-white/10  text-sm shadow-lg"
//           />
//         </div>

//         {/* Table */}
//        <div className="overflow-x-auto rounded-md">
//           <table className="min-w-[820px] w-full border-collapse rounded-md text-sm shadow-lg">
//             <thead className=" text-white border-b border-white/10">
//               <tr>
//                 <th className="text-left w-[260px]">Name</th>
//                 <th className=" text-left w-[260px]">Email</th>
//                 <th className=" text-left w-[110px]">Role</th>
//                 <th className="text-left w-[180px]">Job</th>
//                 <th className="text-left w-[100px]">Joined</th>
//                 <th className="text-left w-[80px] ">Edit</th>
//                 <th className="text-left w-[90px] ">Delete</th>
//               </tr>
//             </thead>

//             <tbody>
//               {loading && (
//                 <tr>
//                   <td colSpan="7" className="py-6 text-center opacity-60">
//                     Loading...
//                   </td>
//                 </tr>
//               )}

//               {!loading &&
//                 // filteredEmployees.map((emp) => (
//                   sortedEmployees.map((emp) => (

//                   <tr
//                     key={emp.id}
//                     className="border-b py-5 border-white/5 hover:bg-white/5"
//                   >
//                     {/* Name */}
//                     <td className=" py-2 w-[260px]">
//                       <div
//                         className="flex items-center gap-3 cursor-pointer"
//                         onClick={() => navigate(`/users/${emp.id}`)}
//                       >
//                         <div className="w-8 h-8 rounded-full bg-white/20 overflow-hidden">
//                           {emp.profile_pic ? (
//   <img
//     src={`${IMAGE_BASE_URL}${emp.profile_pic}`}
//     className="w-full h-full object-cover"
//   />
// ) : (
//   <div className="w-full h-full flex items-center justify-center bg-white/10">
//     <User size={18} className="text-white/60" />
//   </div>
// )}

//                         </div>

//                         <span className="font-medium truncate">
//                           {emp.first_name} {emp.last_name}
//                         </span>
//                       </div>
//                     </td>

//                     {/* Email */}
//                     <td className="w-[260px] truncate opacity-80">
//                       {emp.email}
//                     </td>

//                     {/* Role */}
//                     <td className="w-[110px]">
//                       <span className="
//                         px-2 py-1 rounded-full
//                         bg-blue-500/20 text-blue-300
//                         text-xs
//                       ">
//                         {emp.role_name}
//                       </span>
//                     </td>

//                     {/* Job */}
//                     <td className="w-[180px] truncate">
//                       {emp.job_title || "—"}
//                     </td>

//                     {/* Joined */}
//                     <td className="w-[100px] text-xs opacity-70">
//                       {formatDate(emp.created_at)}
//                     </td>

//                     {/* Edit */}
//                     <td className="w-[80px] text-center">
//                       <button
//                         // onClick={() => navigate(`/users/${emp.id}/edit`)}
//                         onClick={() => setEditUser(emp)}
//                         className="px-3 py-1 rounded bg-white/10 hover:bg-white/20 text-xs"
//                       >
//                         Edit
//                       </button>
//                     </td>

//                     {/* Delete */}
//                     <td className="w-[90px] text-center">
//                       <button
//                         onClick={() => setDeleteUser(emp)}
//                         className="px-3 py-1 rounded bg-red-500/20 text-red-300 hover:bg-red-500 hover:text-white text-xs"
//                       >
//                         Delete
//                       </button>
//                     </td>
//                   </tr>
//                 ))}
//             </tbody>
//           </table>
//         </div>
//       </div>

//       {/* Delete Modal */}
    
// {deleteUser &&
//   createPortal(
//     <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-md">
      
//       <div
//         className="
//           relative
//           w-full max-w-md
//           p-8
//           rounded-2xl
//           bg-white/10
//           backdrop-blur-xl
//           border border-white/20
//           shadow-[0_0_40px_rgba(0,0,0,0.6)]
//           text-white
//         "
//       >
//         {/* Soft glow border effect */}
//         <div className="absolute inset-0 rounded-2xl border border-white/10 pointer-events-none"></div>

//         <h3 className="text-xl font-semibold mb-3 tracking-wide">
//           Delete Employee
//         </h3>

//         <p className="text-sm text-white/70 mb-8">
//           Are you sure you want to delete{" "}
//           <span className="font-medium text-white">
//             {deleteUser.first_name} {deleteUser.last_name}
//           </span>
//           ?
//         </p>

//         <div className="flex justify-end gap-4">
//           <button
//             onClick={() => setDeleteUser(null)}
//             className="
//               px-6 py-2 rounded-lg
//               bg-white/15
//               hover:bg-white/25
//               transition-all duration-200
//             "
//           >
//             Cancel
//           </button>

//           <button
//             onClick={confirmDelete}
//             disabled={deleting}
//             className="
//               px-6 py-2 rounded-lg
//               bg-red-500/80
//               hover:bg-red-600
//               transition-all duration-200
//               disabled:opacity-50
//             "
//           >
//             {deleting ? "Deleting..." : "Delete"}
//           </button>
//         </div>
//       </div>
//     </div>,
//     document.body
//   )}



// {editUser && (
//   <EditUser
//     user={editUser}
//     onClose={() => setEditUser(null)}
//     onUpdated={() => {
//       setEditUser(null);
//       fetchEmployees(); // refresh table after update
//     }}
//   />
// )}

//     </>
//   );
// }






